import { z } from "zod";

// Form model for the svc-10 cost-model editor. Editable: label,
// additionalInvestment, priceRuleSource (the machine-price strategy),
// constants, formulas, priceRuleFilters, priceRuleKeys. Identity + descriptive
// metadata (modelId, appliesTo*, currency, additionalInvestmentCurrency) are
// shown read-only and are NOT part of this form.

/** Machine price strategy — mirrors svc-10 PRICE_RULE_SOURCES. */
export const PRICE_RULE_SOURCES = [
  "avg_base_machine",
  "sherex_price_list_min",
  "profil_indi_config_sum",
  "mds_a240_base_plus_robot",
] as const;

export type PriceRuleSourceValue = (typeof PRICE_RULE_SOURCES)[number];

const numericString = z
  .string()
  .trim()
  .refine((v) => v === "" || /^-?\d+(\.\d+)?$/.test(v), {
    message: "Must be a number",
  });

export const costModelConstantSchema = z.object({
  constantKey: z.string().trim().min(1, "Key is required").max(60),
  value: numericString,
});

export const costModelFormulaSchema = z.object({
  formulaKey: z.string().trim().min(1, "Formula name is required").max(80),
  expression: z.string().max(500),
  evalOrder: z.coerce.number().int().min(0).max(99),
});

export const costModelPriceRuleFilterSchema = z.object({
  filterCategory: z.string().trim().min(1, "Required").max(50),
  filterSubcategory: z.string().trim().min(1, "Required").max(50),
});

export const costModelPriceRuleKeySchema = z.object({
  sherexKey: z.string().trim().min(1, "Required").max(20),
  // Empty string => no explicit sort order.
  sortOrder: z
    .string()
    .trim()
    .refine((v) => v === "" || /^\d+$/.test(v), { message: "Whole number" }),
});

export const costModelSchema = z.object({
  // Identity + descriptive metadata. Editable ONLY in the Add (create) modal;
  // shown read-only in Edit (the page renders them from the detail, not these
  // form fields). All allow "" so the Edit path validates without setting them.
  modelId: z.string().max(60),
  appliesToTargetType: z.string().max(30),
  appliesToTargetId: z.string().max(80),
  appliesToRegion: z.string().max(10),
  currency: z.string().max(10),
  additionalInvestmentCurrency: z.string().max(3),
  label: z.string().max(100),
  additionalInvestment: numericString,
  priceRuleSource: z.enum(PRICE_RULE_SOURCES),
  constants: z.array(costModelConstantSchema),
  formulas: z.array(costModelFormulaSchema),
  priceRuleFilters: z.array(costModelPriceRuleFilterSchema),
  priceRuleKeys: z.array(costModelPriceRuleKeySchema),
});

export type CostModelConstantData = z.infer<typeof costModelConstantSchema>;
export type CostModelFormulaData = z.infer<typeof costModelFormulaSchema>;
export type CostModelPriceRuleFilterData = z.infer<
  typeof costModelPriceRuleFilterSchema
>;
export type CostModelPriceRuleKeyData = z.infer<
  typeof costModelPriceRuleKeySchema
>;
export type CostModelFormData = z.infer<typeof costModelSchema>;
